import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart3, Star, Users, TrendingUp, TrendingDown } from 'lucide-react';
import {
  reactionsService,
  type RatingAnalyticsRow,
} from '@/services/reactions.service';
import {
  productsService,
  type ProductModule,
} from '@/services/products.service';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuthStore } from '@/stores/auth';
import { NativeSelect } from '@/components/ui/select';

function moduleOptions(modules: ProductModule[]): ProductModule[] {
  return modules.flatMap((module) => [
    module,
    ...moduleOptions(module.children ?? []),
  ]);
}

function RatingTable({
  title,
  rows,
  minimum,
}: {
  title: string;
  rows: RatingAnalyticsRow[];
  minimum: number;
}) {
  const filtered = rows.filter((row) => row.count >= minimum);
  return (
    <Card>
      <CardContent className="p-0">
        <h2 className="border-b p-5 text-lg font-semibold">{title}</h2>
        {filtered.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/30 text-muted-foreground text-left">
                <tr>
                  <th className="p-4">Conteúdo</th>
                  <th className="p-4">Média</th>
                  <th className="p-4">Avaliações</th>
                  <th className="p-4">1–2 estrelas</th>
                  <th className="p-4">4–5 estrelas</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.map((row) => (
                  <tr key={row.id}>
                    <td className="max-w-sm p-4 font-medium">{row.title}</td>
                    <td className="whitespace-nowrap p-4">
                      {row.average?.toFixed(2) ?? '—'} / 5
                    </td>
                    <td className="p-4 tabular-nums">{row.count}</td>
                    <td className="p-4">
                      {(row.distribution['1'] ?? 0) +
                        (row.distribution['2'] ?? 0)}
                    </td>
                    <td className="p-4">
                      {(row.distribution['4'] ?? 0) +
                        (row.distribution['5'] ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-muted-foreground p-6 text-sm">
            Nenhuma avaliação corresponde aos filtros.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function AdminRatingsPage() {
  const user = useAuthStore((state) => state.user);
  const [courseId, setCourseId] = useState('');
  const [moduleId, setModuleId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [minimum, setMinimum] = useState(1);
  const [view, setView] = useState<'lessons' | 'modules' | 'courses'>(
    'lessons',
  );
  const allowed = user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN';
  const courses = useQuery({
    queryKey: ['rating-course-options'],
    queryFn: () => productsService.getAllForAdmin(),
    enabled: allowed,
  });
  const modules = useQuery({
    queryKey: ['rating-module-options', courseId],
    queryFn: () => productsService.getModules(courseId),
    enabled: allowed && !!courseId,
  });
  const filters = {
    courseId: courseId || undefined,
    moduleId: moduleId || undefined,
    from: from ? new Date(`${from}T00:00:00`).toISOString() : undefined,
    to: to ? new Date(`${to}T23:59:59.999`).toISOString() : undefined,
  };
  const analytics = useQuery({
    queryKey: ['rating-analytics', user?.id, filters],
    queryFn: () => reactionsService.getRatingAnalytics(filters),
    enabled: allowed,
  });
  const data = analytics.data;
  if (!allowed) return <p role="alert">Acesso restrito à administração.</p>;

  return (
    <div className="space-y-6">
      <header>
        <p className="text-primary mb-2 text-xs font-semibold uppercase tracking-widest">
          Qualidade de ensino
        </p>
        <h1 className="text-3xl font-bold">Avaliações das aulas</h1>
        <p className="text-muted-foreground mt-2">
          Entenda o que funciona e encontre os conteúdos que precisam de
          atenção.
        </p>
      </header>
      <Card>
        <CardContent className="grid gap-4 pt-6 sm:grid-cols-2 xl:grid-cols-5">
          <label className="space-y-2 text-sm">
            Curso
            <NativeSelect
              aria-label="Curso"
              value={courseId}
              onChange={(event) => {
                setCourseId(event.target.value);
                setModuleId('');
              }}
            >
              <option value="">Todos os cursos</option>
              {courses.data?.map((course) => (
                <option key={course.id} value={course.id}>
                  {course.title}
                </option>
              ))}
            </NativeSelect>
          </label>
          <label className="space-y-2 text-sm">
            Módulo
            <NativeSelect
              aria-label="Módulo"
              value={moduleId}
              onChange={(event) => setModuleId(event.target.value)}
              disabled={!courseId}
            >
              <option value="">Todos os módulos</option>
              {moduleOptions(modules.data ?? []).map((module) => (
                <option key={module.id} value={module.id}>
                  {module.title}
                </option>
              ))}
            </NativeSelect>
          </label>
          <label className="space-y-2 text-sm">
            De
            <Input
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
            />
          </label>
          <label className="space-y-2 text-sm">
            Até
            <Input
              type="date"
              value={to}
              min={from}
              onChange={(event) => setTo(event.target.value)}
            />
          </label>
          <label className="space-y-2 text-sm">
            Mínimo de votos
            <Input
              type="number"
              min={1}
              value={minimum}
              onChange={(event) =>
                setMinimum(Math.max(1, Number(event.target.value) || 1))
              }
            />
          </label>
        </CardContent>
      </Card>
      {(courses.isError || modules.isError) && (
        <p role="alert" className="text-destructive text-sm">
          Não foi possível carregar as opções de filtro.{' '}
          <Button variant="link" size="sm" type="button"
            onClick={() => {
              void courses.refetch();
              if (courseId) void modules.refetch();
            }}
          >
            Tentar novamente
          </Button>
        </p>
      )}
      {analytics.isLoading ? (
        <p role="status">Carregando avaliações…</p>
      ) : analytics.isError ? (
        <div role="alert" className="rounded-lg border p-6">
          Não foi possível consultar as avaliações.
          <Button
            className="ml-3"
            variant="outline"
            onClick={() => void analytics.refetch()}
          >
            Tentar novamente
          </Button>
        </div>
      ) : (
        data && (
          <>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                {
                  label: 'Média geral',
                  value: data.summary.average?.toFixed(2) ?? '—',
                  detail: 'de 5 estrelas',
                  icon: Star,
                },
                {
                  label: 'Avaliações recebidas',
                  value: data.summary.count,
                  detail: 'um voto por aluno e aula',
                  icon: Users,
                },
                {
                  label: 'Aulas avaliadas',
                  value: data.lessons.length,
                  detail: 'no período selecionado',
                  icon: BarChart3,
                },
                {
                  label: 'Satisfação',
                  value: data.summary.count
                    ? `${Math.round(((data.summary.distribution['4'] + data.summary.distribution['5']) / data.summary.count) * 100)}%`
                    : '—',
                  detail: 'votos de 4 ou 5 estrelas',
                  icon: TrendingUp,
                },
              ].map((item) => (
                <Card key={item.label}>
                  <CardContent className="pt-6">
                    <item.icon className="text-primary mb-3 h-5 w-5" />
                    <p className="text-muted-foreground text-sm">
                      {item.label}
                    </p>
                    <p className="my-2 text-3xl font-bold tabular-nums">
                      {item.value}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {item.detail}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <Card>
              <CardContent className="space-y-3 pt-6">
                <h2 className="text-lg font-semibold">
                  Distribuição das notas
                </h2>
                {[5, 4, 3, 2, 1].map((rating) => {
                  const count = data.summary.distribution[rating] ?? 0;
                  const percent = data.summary.count
                    ? (count / data.summary.count) * 100
                    : 0;
                  return (
                    <div
                      key={rating}
                      className="flex items-center gap-3 text-sm"
                    >
                      <span className="w-20">{rating} estrelas</span>
                      <div className="bg-muted h-3 flex-1 overflow-hidden rounded-full">
                        <div
                          className="bg-primary h-full rounded-full"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                      <span className="w-28 text-right tabular-nums">
                        {count} · {percent.toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
            <div className="grid gap-6 xl:grid-cols-2">
              <div className="space-y-3">
                <h2 className="flex items-center gap-2 font-semibold">
                  <TrendingUp className="text-primary h-4 w-4" />
                  Destaques
                </h2>
                <RatingTable
                  title="Melhores aulas"
                  rows={[...data.lessons]
                    .filter((row) => row.count >= minimum)
                    .sort(
                      (a, b) =>
                        (b.average ?? 0) - (a.average ?? 0) ||
                        b.count - a.count,
                    )
                    .slice(0, 10)}
                  minimum={minimum}
                />
              </div>
              <div className="space-y-3">
                <h2 className="flex items-center gap-2 font-semibold">
                  <TrendingDown className="h-4 w-4 text-amber-500" />
                  Oportunidades de melhoria
                </h2>
                <RatingTable
                  title="Menores notas"
                  rows={[...data.lessons]
                    .filter((row) => row.count >= minimum)
                    .sort(
                      (a, b) =>
                        (a.average ?? 0) - (b.average ?? 0) ||
                        b.count - a.count,
                    )
                    .slice(0, 10)}
                  minimum={minimum}
                />
              </div>
            </div>
            <div className="flex gap-2" aria-label="Agrupamento das avaliações">
              {(['lessons', 'modules', 'courses'] as const).map((group) => (
                <Button
                  key={group}
                  variant={view === group ? 'default' : 'outline'}
                  onClick={() => setView(group)}
                  aria-pressed={view === group}
                >
                  {group === 'lessons'
                    ? 'Aulas'
                    : group === 'modules'
                      ? 'Módulos'
                      : 'Cursos'}
                </Button>
              ))}
            </div>
            <RatingTable
              title={
                view === 'lessons'
                  ? 'Todas as aulas'
                  : view === 'modules'
                    ? 'Média por módulo'
                    : 'Média por curso'
              }
              rows={data[view]}
              minimum={minimum}
            />
            <p className="text-muted-foreground text-xs">
              As médias usam votos individuais, sem fazer média de médias. Aulas
              compartilhadas contam uma vez por curso. O período considera a
              última atualização do voto; avaliações antigas por reações não
              representam notas exatas.
            </p>
          </>
        )
      )}
    </div>
  );
}
